import { h } from "preact";
import { useCallback, useEffect, useState } from "preact/hooks";
import { RoutableProps } from "preact-router";
import { ScanPackQR } from "../../../components/ScanPackQR";
import "./styles.scss";
import { resolvePath } from "../../../api";
import { TemplatePrint } from "../../../components/TemplatePrint";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { useCommand } from "../../../hooks";
import { getProductTypeRevision, getSerialNumberInfo, Label, Product, ProductTypeRevision } from "../../../api/product-api";
import { manufacturingConfigurations } from "../../../App";
import { findSerialNumberByRegex, generateLabelFields } from "../../../utils";
import { meissaOT } from "../../../data/serialNumberSearchExecs";
import { filterDefaultLabelsForRevision } from "../../../data/filterDefaultLabelsForRevision";

export function MeissaOTReprint(props: RoutableProps) {
    const [serialNumber, setSerialNumber] = useState("");
    const [typeInfo, setTypeInfo] = useState(null as { product: Product, revision: ProductTypeRevision } | null);
    const showSnackbar = useCommand<SnackbarShowCommand>();
    const [selectedLabel, setSelectedLabel] = useState(null as null | Label);

    useEffect(() => {
        if (!serialNumber) {
            setTypeInfo(null);
            setSelectedLabel(null);
            return;
        }

        if (!findSerialNumberByRegex(serialNumber, [
            meissaOT.execs.elementSerialRegexPlain
        ])) {
            setTypeInfo(null);
            setSelectedLabel(null);
            showSnackbar.run("Podany numer seryjny jest w złym formacie", "error", "error", 2000);
            return;
        }

        getSerialNumberInfo([serialNumber]).then(async (products) => {
            if (products.length === 0) {
                setTypeInfo(null);
                setSelectedLabel(null);
                showSnackbar.run("Podany numer seryjny nie istnieje", "error", "error", 2000);
                return;
            }

            const product = products[0];

            const types = manufacturingConfigurations["MeissaOT"];
            const foundType = types?.find((v) => v.productType.name === product.productTypeRevision.productType!.name);

            if (!foundType) {
                setTypeInfo(null);
                setSelectedLabel(null);
                showSnackbar.run("Podany numer seryjny jest nieprawidłowy", "error", "error", 2000)
                return;
            }

            const revision = await getProductTypeRevision(product.productTypeRevision.id);
            showSnackbar.run("Znaleziono numer seryjny", "success", "check", 1000);
            setTypeInfo({ product, revision });
            setSelectedLabel(filterDefaultLabelsForRevision(revision)[0]?.label ?? null);
        });
    }, [serialNumber]);

    const onReadCallback = useCallback((data: string) => {
        const text = JSON.parse(data) as string;
        const serial = findSerialNumberByRegex(text, meissaOT.orderedExecs);

        if (!serial) {
            return;
        }

        setSerialNumber(serial);
    }, []);

    return <div class="mezos-sit-reprint-page">
        <Snackbar doShow={showSnackbar}/>
        <div class="paper serial-input">
            <ScanPackQR onRead={onReadCallback}/>
            <div>
                <label>
                    <span style="margin-right: 10px;">Podaj lub zeskanuj numer seryjny naklejki, którą chcesz wydrukować:</span>
                    <input value={serialNumber} onChange={e => setSerialNumber(e.currentTarget.value.trim())}/>
                </label>
            </div>
        </div>
        {typeInfo && <div class="paper" style="padding: 10px; margin-top: 10px; margin-left: 10px; margin-right: 10px">
            <h1>Naklejki</h1>
            {typeInfo.revision.productTypeRevisionLabels!.filter(v => v.label.description.includes("(archived)")).length > 0 && <div class="accordion" style="margin-top: 10px;">
                <div>
                    <input type="radio" name="accordion" />
                    <input type="radio" name="accordion" />
                    <div class="header">Zarchiwizowane</div>
                    <div class="content" style="flex-direction: column">
                        {typeInfo.revision.productTypeRevisionLabels!.filter(v => v.label.description.includes("(archived)")).map(v =>
                            <div class={"item" + (v.label === selectedLabel ? " selected" : "")} onClick={() => setSelectedLabel(v.label)}>
                                <div style="font-size: 16px;">{v.label.name}</div>
                                <div style="flex: 1 1;"/>
                                {v.label.description.includes("(archived)") ? <div class="chip warning">Zarchiwizowane</div> : null}
                                {v.label.metadata && v.label.metadata.metadata["tape-type"] ? <div class="chip secondary" style="margin-left: 10px;">{v.label.metadata.metadata["tape-type"]}</div> : null}
                                {v.label.metadata && v.label.metadata.metadata["tape-width"] ? <div class="chip secondary" style="margin-left: 10px;">{v.label.metadata.metadata["tape-width"]}</div> : null}
                            </div>)}
                    </div>
                </div>
            </div>}
            <div class="label-list list primary">
                {typeInfo.revision.productTypeRevisionLabels!.filter(v => !v.label.description.includes("(archived)")).map(v =>
                <div class={"item" + (v.label === selectedLabel ? " selected" : "")} onClick={() => setSelectedLabel(v.label)}>
                    <div style="font-size: 16px;">{v.label.name}</div>
                    <div style="flex: 1 1;"/>
                    {v.label.metadata && v.label.metadata.metadata["tape-type"] ? <div class="chip secondary" style="margin-left: 10px;">{v.label.metadata.metadata["tape-type"]}</div> : null}
                    {v.label.metadata && v.label.metadata.metadata["tape-width"] ? <div class="chip secondary" style="margin-left: 10px;">{v.label.metadata.metadata["tape-width"]}</div> : null}
                </div>)}
            </div>
            {typeInfo.revision.productTypeRevisionLabels!.length === 0 ? <div style="margin-top: 10px;">
                Brak naklejek dla tej rewizji
            </div> : null}
        </div>}
        {typeInfo && selectedLabel ? <div class="paper print-serial">
            <TemplatePrint fields={{
                ...generateLabelFields({
                    date: new Date(Date.parse((console.log(typeInfo.product), typeInfo.product.createdAt))),
                    serialNumber,
                    revision: typeInfo.revision
                })
            }}
            template={selectedLabel.path}
            overrideFields={selectedLabel.metadata?.metadata}
            tapeImgBase={resolvePath("images/tapes/")}/>
        </div>: null}
    </div>;
}
